import React, { useCallback, useEffect, useReducer } from 'react';
import type { ReactElement, Reducer } from 'react';
import { Box, useInput } from 'ink';
import { Text } from '../Text.js';
import { Indicator } from './Indicator.js';
import { Item } from './Item.js';
import type { ListItem } from './helpers.js';
import { getVisibleListItems, getRowCount, GROUP_PADDING } from './helpers.js';
import { Scroll } from '../Scroll.js';
import { Highlighter } from './Highlighter.js';
import type { SelectOption } from './types.js';

const BOTTOM_SCROLL_OFFSET = 1;

interface GroupsListProps<T> {
  listItems: Array<ListItem<T>>;
  highlightPattern?: RegExp;
  onItemSelect: (item: SelectOption<T>) => void;
  maxHeight: number;
  width?: number | string;
  hideScroll?: boolean;
}

interface GroupsListState<T> {
  listItems: Array<ListItem<T>>;
  visibleItems: Array<ListItem<T>>;
  currentIndex: number;
  totalRowCounts: number;
  maxHeight: number;
}

enum GroupsListActionType {
  MOVE_UP = 'MOVE_UP',
  MOVE_DOWN = 'MOVE_DOWN',
  REFRESH = 'REFRESH',
  CHANGE_MAX_HEIGHT = 'CHANGE_MAX_HEIGHT',
}

type GroupsListAction<T> =
  | {
      type:
        | GroupsListActionType.MOVE_UP
        | GroupsListActionType.MOVE_DOWN
        | GroupsListActionType.CHANGE_MAX_HEIGHT;
      payload: number;
    }
  | {
      type: GroupsListActionType.REFRESH;
      payload: Array<ListItem<T>>;
    };

function reducer<T>(
  state: GroupsListState<T>,
  action: GroupsListAction<T>
): GroupsListState<T> {
  const { type, payload } = action;

  switch (type) {
    case GroupsListActionType.CHANGE_MAX_HEIGHT: {
      const maxHeight = payload;
      return {
        ...state,
        currentIndex: 0,
        maxHeight,
        visibleItems: getVisibleListItems<T>(0, state.listItems, maxHeight),
      };
    }
    case GroupsListActionType.REFRESH: {
      const listItems = payload;
      return {
        listItems,
        currentIndex: 0,
        totalRowCounts: getRowCount(listItems),
        maxHeight: state.maxHeight,
        visibleItems: getVisibleListItems<T>(0, listItems, state.maxHeight),
      };
    }
    case GroupsListActionType.MOVE_DOWN: {
      const nextIndex = payload;
      if (
        nextIndex + BOTTOM_SCROLL_OFFSET < state.listItems.length &&
        nextIndex + BOTTOM_SCROLL_OFFSET > state.visibleItems.at(-1)!.itemIndex
      ) {
        return {
          ...state,
          currentIndex: nextIndex,
          visibleItems: getVisibleListItems<T>(
            nextIndex + BOTTOM_SCROLL_OFFSET,
            state.listItems,
            state.maxHeight,
            true
          ),
        };
      }

      if (nextIndex > state.visibleItems.at(-1)!.itemIndex) {
        return {
          ...state,
          currentIndex: nextIndex,
          visibleItems: getVisibleListItems<T>(
            nextIndex,
            state.listItems,
            state.maxHeight,
            true
          ),
        };
      }

      return {
        ...state,
        currentIndex: nextIndex,
      };
    }
    case GroupsListActionType.MOVE_UP: {
      const nextIndex = payload;
      if (
        state.visibleItems[0] &&
        nextIndex < state.visibleItems[0].itemIndex
      ) {
        return {
          ...state,
          currentIndex: nextIndex,
          visibleItems: getVisibleListItems<T>(
            nextIndex,
            state.listItems,
            state.maxHeight
          ),
        };
      }

      return {
        ...state,
        currentIndex: nextIndex,
      };
    }
    default:
      return state;
  }
}

export function GroupsList<T>({
  listItems: listItemsProp,
  highlightPattern,
  onItemSelect,
  hideScroll,
  width,
  maxHeight: maxHeightProp,
}: GroupsListProps<T>) {
  const [state, dispatch] = useReducer<
    Reducer<GroupsListState<T>, GroupsListAction<T>>
  >(reducer, {
    listItems: [],
    visibleItems: [],
    currentIndex: 0,
    totalRowCounts: 0,
    maxHeight: maxHeightProp,
  });
  const { listItems, visibleItems, currentIndex, maxHeight, totalRowCounts } =
    state;

  useEffect(() => {
    dispatch({
      type: GroupsListActionType.REFRESH,
      payload: listItemsProp,
    });
  }, [listItemsProp]);

  useEffect(() => {
    dispatch({
      type: GroupsListActionType.CHANGE_MAX_HEIGHT,
      payload: maxHeightProp,
    });
  }, [maxHeightProp]);

  useInput(
    useCallback(
      (input, key) => {
        if (key.upArrow) {
          if (currentIndex > 0) {
            dispatch({
              type: GroupsListActionType.MOVE_UP,
              payload: currentIndex - 1,
            });
          } else {
            dispatch({
              type: GroupsListActionType.MOVE_DOWN,
              payload: listItems.length - 1,
            });
          }
        }

        if (key.downArrow) {
          if (currentIndex < listItems.length - 1) {
            dispatch({
              type: GroupsListActionType.MOVE_DOWN,
              payload: currentIndex + 1,
            });
          } else {
            dispatch({
              type: GroupsListActionType.MOVE_UP,
              payload: 0,
            });
          }
        }

        if (key.return) {
          const listItem = listItems[currentIndex];

          if (listItem) {
            onItemSelect(listItem.item);
          }
        }
      },
      [currentIndex, listItems, onItemSelect]
    ),
    { isActive: true }
  );

  const shouldShowScroll =
    !hideScroll && totalRowCounts > maxHeight && visibleItems.length;
  const viewHeight = Math.min(totalRowCounts, maxHeight);

  return (
    <Box
      height={viewHeight}
      flexDirection="row"
      justifyContent="space-between"
      width={width ?? '100%'}
    >
      <Box flexDirection="column" height={viewHeight}>
        {visibleItems.reduce<ReactElement[]>(
          (acc, { group, groupIndex, item, itemIndex }, index) => {
            const shouldShowGroupLabel =
              index === 0 ||
              groupIndex !== visibleItems.at(index - 1)?.groupIndex;
            if (shouldShowGroupLabel) {
              acc.push(
                <Box
                  key={`group-${groupIndex}`}
                  paddingTop={index === 0 ? 0 : GROUP_PADDING}
                >
                  <Text bold underline>
                    <Highlighter
                      pattern={highlightPattern}
                      text={group.title}
                    />
                  </Text>
                  {group.description && (
                    <Box marginLeft={1}>{group.description}</Box>
                  )}
                </Box>
              );
            }
            acc.push(
              <Box key={`item-${itemIndex}`} marginLeft={1}>
                <Indicator isSelected={itemIndex === currentIndex} />
                <Item
                  label={
                    <Highlighter pattern={highlightPattern} text={item.title} />
                  }
                  isSelected={itemIndex === currentIndex}
                  value={item}
                />
              </Box>
            );
            return acc;
          },
          []
        )}
      </Box>
      {shouldShowScroll ? (
        <Box flexDirection="column" height={viewHeight}>
          <Scroll
            height={viewHeight}
            // -1 because before first item we also show group title
            firstShownRow={visibleItems.at(0)!.rowIndex - 1}
            totalRowCounts={totalRowCounts}
          />
        </Box>
      ) : null}
    </Box>
  );
}
